import { Box, Image, Typography } from "@outschool/backpack";
import {
  ActivityUnderReviewQuery,
  ClassDetailsQuery_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { SimpleVideo } from "@outschool/ui-components-website";
import {
  Modal,
  ModalActionButtons,
} from "@outschool/ui-legacy-component-library";
import React from "react";

import playSvg from "../../../../images/play.svg";

export interface PlayVideoOverlayButtonProps {
  activity:
    | ActivityUnderReviewQuery["activity"]
    | ClassDetailsQuery_ActivityFragment;
  isLearnerVideo?: boolean;
  clickableAreaIsButtonOnly?: boolean;
}

export default function PlayVideoOverlayButton({
  activity,
  isLearnerVideo,
  clickableAreaIsButtonOnly = false,
}: PlayVideoOverlayButtonProps) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\PlayVideoOverlayButton",
  );
  const [open, setOpen] = React.useState(false);
  const segmentTrack = useTrackEvent();

  const handlePlayClick = React.useCallback(() => {
    segmentTrack("Watched Class About Video", {
      activityUid: activity.uid,
    });
    setOpen(true);
  }, [activity.uid, segmentTrack]);

  if (!activity) {
    return null;
  }

  const video = isLearnerVideo
    ? (activity as ActivityUnderReviewQuery["activity"])?.videoForLearners
    : activity?.video;
  if (!video) {
    return null;
  }

  return (
    <>
      <Box
        flex
        sx={{
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          cursor: clickableAreaIsButtonOnly ? "default" : "pointer",
        }}
        onClick={clickableAreaIsButtonOnly ? null : handlePlayClick}
        data-test-id="play-video-overlay-button"
      >
        <Image
          src={playSvg}
          alt={t("Play")}
          width="64"
          height="64"
          sx={{ cursor: clickableAreaIsButtonOnly ? "pointer" : "default" }}
          onClick={clickableAreaIsButtonOnly ? handlePlayClick : undefined}
          data-test-id="play-overlay"
        />
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Typography variant="h3">
          {t("About {{activityTitle}}", {
            activityTitle: activity.title,
          })}
        </Typography>
        <SimpleVideo
          title={t("About {{activityTitle}}", {
            activityTitle: activity.title,
          })}
          src={video.file.url}
          poster={video.file.thumbUrl || undefined}
          autoPlay
          sx={{ marginBottom: "2em" }}
        />
        <ModalActionButtons
          primaryLabel={t("Close")}
          onPrimary={() => setOpen(false)}
          hideCancelButton
        />
      </Modal>
    </>
  );
}
